import { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { isMasterAdmin } from '../services/masterAdmin'

const MasterAdminContext = createContext(null)

export const useMasterAdmin = () => {
  const context = useContext(MasterAdminContext)
  if (!context) {
    throw new Error('useMasterAdmin must be used within MasterAdminProvider')
  }
  return context
}

export const MasterAdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    const checkMasterAdmin = async () => {
      try {
        const adminStatus = await isMasterAdmin()
        if (mounted) {
          setIsAdmin(adminStatus)
        }
      } catch (error) {
        // Silently fail - don't log errors for non-master-admin users
        if (mounted) {
          setIsAdmin(false)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    // Only check if user is authenticated
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user && mounted) {
        checkMasterAdmin()
      } else if (mounted) {
        setIsAdmin(false)
        setLoading(false)
      }
    })

    // Recheck on auth state change (but debounce to avoid excessive calls)
    // CRITICAL: Ignore TOKEN_REFRESHED events to prevent reloads on tab switch
    // Note: App.jsx handles duplicate SIGNED_IN detection, this is a secondary check
    let authCheckTimeout
    let lastUserId = null
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Don't react to TOKEN_REFRESHED - it happens on visibility change and causes reloads
      if (event === 'TOKEN_REFRESHED') {
        return // Ignore token refresh events
      }
      
      const currentUserId = session?.user?.id
      const isDuplicateSignIn = event === 'SIGNED_IN' && lastUserId === currentUserId
      
      // Ignore duplicate SIGNED_IN events (session restoration)
      if (isDuplicateSignIn) {
        return
      }
      
      // Update lastUserId for next comparison
      if (event === 'SIGNED_IN') {
        lastUserId = currentUserId
      } else if (event === 'SIGNED_OUT') {
        lastUserId = null
      }
      
      // Clear previous timeout
      if (authCheckTimeout) {
        clearTimeout(authCheckTimeout)
      }
      
      // Debounce the check
      authCheckTimeout = setTimeout(() => {
        if (session?.user && mounted) {
          checkMasterAdmin()
        } else if (mounted) {
          setIsAdmin(false)
        }
      }, 500)
    })

    return () => {
      mounted = false
      if (authCheckTimeout) {
        clearTimeout(authCheckTimeout)
      }
      subscription.unsubscribe()
    }
  }, [])

  const value = {
    isMasterAdmin: isAdmin,
    loading,
  }

  return <MasterAdminContext.Provider value={value}>{children}</MasterAdminContext.Provider>
}

